import { AutoStack, FlexSpinner } from 'components'
import { DownloadLink } from 'components/DownloadLink'
import { Container } from 'components/FileUpload/FileUpload'
import { RichTextViewer } from 'components/RichTextViewer'
import { FormikTagPicker } from 'components/TagPicker/FormikTagPicker'
import { TextEditor } from 'components/TextEditor'
import { FieldArray } from 'formik'
import { FormikCheckbox, FormikTextField } from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import { Box } from 'grid'
import { useCurrentFormik } from 'hooks/useCurrentFormikContext'
import { useProvingGroundDropdownQuery } from 'model'
import { IconButton, ITag, Label, Text } from 'office-ui-fabric-react'
import * as React from 'react'
import Dropzone from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import {
  FILE_COMPONENTS,
  getProperty,
} from 'views/AdminWikiView/WikiListSingleView'

export interface WikiFormValues {
  provingGround?: string
  topic: string
  files?: string[]
  inactive?: boolean
  tag?: string
  body?: string
  uploadFiles?: any[]
  uploadPreview?: any
}

interface WikiProps {
  disabled: boolean
  filesUploading?: boolean
}
export const WikiForm: React.FC<WikiProps> = ({
  disabled,
  filesUploading,
}: WikiProps) => {
  const { t } = useTranslation()
  const formikContext = useCurrentFormik()
  const initialValues = formikContext ? formikContext.initialValues : undefined
  const { loading, error, data } = useProvingGroundDropdownQuery()
  const provingGrounds =
    !loading && data && data.provingGrounds ? data.provingGrounds : []
  const provingGroundOptions: ITag[] = [
    { key: '', name: t('model.wiki.general') },
    ...provingGrounds.map(
      (pg) =>
        ({
          key: pg._id,
          name: pg.title,
        } as ITag)
    ),
  ]

  return (
    <FormStack>
      <FieldStack>
        <FlexSpinner
          loading={filesUploading}
          error={error}
          label={t('Bitte warten, Datei(en) werden hochgeladen...')}
          labelPosition="left"
          paddingTop={2}
        >
          {initialValues && initialValues.topic && (
            <Text>KSU-Klasse: {initialValues.deletionRule}</Text>
          )}
          <FieldStackItem
            label={t('field.topic.label')}
            name="topic"
            disabled={disabled}
            required={true}
            component={FormikTextField}
          />

          <FieldStackItem
            name="provingGround"
            disabled={disabled}
            render={(fieldProps) => (
              <>
                <Label disabled={disabled}>
                  {t('field.provingGround.label')}
                </Label>
                <FormikTagPicker
                  {...fieldProps}
                  tags={provingGroundOptions}
                  disabled={disabled}
                />
              </>
            )}
          />
          <FieldStackItem
            label={t('field.tag.label')}
            name="tag"
            disabled={disabled}
            component={FormikTextField}
          />
          <FieldStackItem
            name="body"
            render={(fieldProps) => (
              // wait till the body is ready, because the editor value can only
              // be set={true} during={true} the={true} first={true} render={true}
              <>
                {disabled ? (
                  <>
                    <Label>{t('field.body.label')}</Label>
                    <RichTextViewer richText={fieldProps.field.value} />
                  </>
                ) : (
                  <>
                    <Label>{t('field.body.label')}</Label>
                    <TextEditor
                      value={fieldProps.field.value}
                      onChange={(value) => {
                        fieldProps.form.setFieldValue('body', value || '')
                      }}
                    />
                  </>
                )}
              </>
            )}
          />

          <FieldArray name="files">
            {({ form, name, ...helpers }) => {
              const files: string[] = form.values.files || []
              return (
                <AutoStack tokens={{ childrenGap: 12 }}>
                  {files.length > 0 && <Label>{t('field.files.label')}</Label>}
                  {files.map((file: string, i: number) => (
                    <FieldStack horizontal={true} key={i}>
                      <FieldStackItem
                        name={`files[${i}]`}
                        key={`files[${i}]`}
                        render={(_) => (
                          <DownloadLink
                            styles={{
                              root: { alignSelf: 'start', paddingLeft: '1vw' },
                            }}
                            uri={getProperty(FILE_COMPONENTS.uri, file) || ''}
                          >
                            {getProperty(FILE_COMPONENTS.fileName, file) || ''}
                          </DownloadLink>
                        )}
                      />
                      {!disabled && (
                        <IconButton
                          disabled={disabled}
                          styles={{
                            root: { color: 'red', marginTop: 'auto' },
                          }}
                          iconProps={{ iconName: 'Delete' }}
                          onClick={() => helpers.remove(i)}
                        />
                      )}
                    </FieldStack>
                  ))}
                </AutoStack>
              )
            }}
          </FieldArray>

          {!disabled && (
            <>
              <FieldStackItem
                name="uploadFiles"
                render={(fieldProps) => (
                  <div>
                    <Label disabled={disabled}>
                      {t('field.uploadFiles.label')}
                    </Label>
                    <Container>
                      <Dropzone
                        disabled={disabled}
                        noDragEventsBubbling={true}
                        onDrop={async (acceptedFiles) => {
                          if (acceptedFiles.length === 0) {
                            return
                          }
                          fieldProps.form.setFieldValue(
                            'uploadFiles',
                            acceptedFiles
                          )
                        }}
                      >
                        {({ getRootProps, getInputProps }) => {
                          if (!fieldProps.form.values.uploadFiles) {
                            return (
                              <section>
                                <div {...getRootProps()}>
                                  <input {...getInputProps()} />
                                  <p>{t('field.fileUpload.defaultText')}</p>
                                </div>
                              </section>
                            )
                          } else {
                            return (
                              <section>
                                <div {...getRootProps()}>
                                  <input {...getInputProps()} />
                                  {fieldProps.form.values.uploadFiles.map(
                                    (uploadFile: { name: string }) => {
                                      return (
                                        <p
                                          key={`upload_data${uploadFile.name}`}
                                        >
                                          {uploadFile.name}
                                        </p>
                                      )
                                    }
                                  )}
                                </div>
                              </section>
                            )
                          }
                        }}
                      </Dropzone>
                      {/* <IconButton
                    styles={{
                      root: { color: 'red', marginTop: 'auto' },
                    }}
                    disabled={
                      !(
                        fieldProps.form.values.fileName ||
                        fieldProps.form.values.file
                      ) || disabled
                    }
                    iconProps={{ iconName: 'Delete' }}
                    onClick={() => {
                      fieldProps.form.setFieldValue('file', undefined)
                      fieldProps.form.setFieldValue('fileName', null)
                      fieldProps.form.setFieldValue('uri', null)
                    }}
                  /> */}
                    </Container>
                  </div>
                )}
              />
              <FieldStackItem
                name="uploadPreview"
                disabled={disabled}
                render={(fieldProps) => (
                  <div>
                    <Label disabled={disabled}>
                      {t('field.uploadPreview.label')}
                    </Label>
                    <Container>
                      <Dropzone
                        accept={[
                          '.img',
                          '.png',
                          '.svg',
                          '.jpg',
                          '.jpeg',
                          '.jfif',
                        ]}
                        multiple={false}
                        disabled={disabled}
                        noDragEventsBubbling={true}
                        onDrop={async (acceptedFiles) => {
                          if (acceptedFiles.length !== 1) {
                            return
                          }
                          if (acceptedFiles[0]) {
                            fieldProps.form.setFieldValue(
                              'uploadPreview',
                              acceptedFiles[0]
                            )
                          }
                        }}
                      >
                        {({ getRootProps, getInputProps }) => {
                          if (!fieldProps.form.values.uploadPreview) {
                            return (
                              <section>
                                <div {...getRootProps()}>
                                  <input {...getInputProps()} />
                                  <p>{t('field.fileUpload.defaultText')}</p>
                                </div>
                              </section>
                            )
                          } else {
                            return (
                              <section>
                                <div {...getRootProps()}>
                                  <input {...getInputProps()} />
                                  {[fieldProps.form.values.uploadPreview].map(
                                    (uploadPreview: { name: string }) => {
                                      return (
                                        <p
                                          key={`upload_data${uploadPreview.name}`}
                                        >
                                          {uploadPreview.name}
                                        </p>
                                      )
                                    }
                                  )}
                                </div>
                              </section>
                            )
                          }
                        }}
                      </Dropzone>
                    </Container>
                  </div>
                )}
              />
              <FieldStack horizontal={true}>
                <FieldStackItem
                  name={`filePreview`}
                  render={(fieldProps) => (
                    <>
                      {fieldProps.field.value && (
                        <>
                          {'Aktuelles Vorschaubild: '}
                          <DownloadLink
                            styles={{
                              root: { alignSelf: 'start', paddingLeft: '1vw' },
                            }}
                            uri={
                              getProperty(
                                FILE_COMPONENTS.uri,
                                fieldProps.field.value
                              ) || ''
                            }
                          >
                            {getProperty(
                              FILE_COMPONENTS.fileName,
                              fieldProps.field.value
                            ) || ''}
                          </DownloadLink>
                        </>
                      )}
                    </>
                  )}
                />
              </FieldStack>
              <FieldStackItem
                stack={{ grow: 0 }}
                name="inactive"
                disabled={disabled}
                render={(fieldProps) => (
                  <>
                    <Label disabled={disabled}>
                      {t('field.inactive.label')}
                    </Label>
                    <Box py={1}>
                      <FormikCheckbox {...fieldProps} disabled={disabled} />
                    </Box>
                  </>
                )}
              />
            </>
          )}
        </FlexSpinner>
      </FieldStack>
    </FormStack>
  )
}
